import z from 'zod'
import { Polygon } from '..'
import { RadiiResponse } from 'src/common-services/types/base-types'
import { MultiPolygon, Point } from 'src/common-services/types/geojson-types'

export type BlockGroup = {
  blockId: string
  cbsaId: string
  state: string
  county: string
  cbsa: string
  centroid: Point
  polygon: MultiPolygon
}

export const demoRadiiRequestSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  radii: z.array(z.number().or(z.string())),
})
export type DemoRadiiRequest = z.infer<typeof demoRadiiRequestSchema>

export type DemoMultiPolygonRequest = {
  polygon: MultiPolygon
}

export type DemoMultiPolygonResponse = DemoRadiiData

const demoDatumSchema = z.object({
  type: z.string().optional(),
  currentYear: z.number().optional(),
  fiveYear: z.number().optional(),
})

export type DemoDatum = z.infer<typeof demoDatumSchema>

export const demoRadiiDataSchema = z.record(z.string(), z.record(z.string(), demoDatumSchema))
export type DemoRadiiData = z.infer<typeof demoRadiiDataSchema>

export type BlockGroupDemographics = {
  id: string
  polygon: MultiPolygon
  data: DemoRadiiData
}

export type BlockGroupDemographicsResponse = BlockGroupDemographics[]

export type DemoRadiiResponse = RadiiResponse<DemoRadiiData>

export type BlockGroupResponse = BlockGroup[]

export type DemoHierarchyInfo = {
  id: string
  name: string
}

export type DemoTimeFrameResponse = {
  timeFrames: DemoHierarchyInfo[]
}

export type DemoGroupResponse = {
  groups: DemoHierarchyInfo[]
}

export type DemoBlockGroupDataRequest = {
  blockGroupId: string
  timeFrameId: string
  groupId: string
}

export type DemoBlockGroupsDataRequest = {
  blockGroupIds: Array<BlockGroup['blockId']>
  timeFrameId: DemoHierarchyInfo['id']
  groupId: DemoHierarchyInfo['id']
}

type DemoCategoryData = { category: string; value: number }

export type BlockGroupDatum = {
  category: string
  column: string
  value: number
}

export type BulkBlockGroupResponse = Record<BlockGroup['blockId'], BlockGroupDatum[]>

export type DemoBlockGroupDataResponse = {
  data: DemoCategoryData[]
  timeFrame: string
  group: string
}

export type DemoH3Request = {
  h3Indexes: bigint[]
  fields?: Array<keyof DemoH3Datum>
}

export type DemoH3ByPolygonRequest = {
  polygon: Polygon
  resolution: number
  fields: Array<keyof DemoH3Datum>
}

export type DemoRawRadiusRequest = {
  radius: number
  lat: number
  lng: number
}

export type DemoRawMultiPolygonRequest = {
  polygon: MultiPolygon
}

type DemoFields = {
  cy_pop_c: number
  cy_work_day_count: number
  cy_househldsc: number
  cy_ppop_gthc: number
  fy_pop_f: number
  cy_white_c: number
  cy_hispanic_c: number
  cy_black_c: number
  cy_asian_c: number
  cy_indian_c: number
  cy_other_c: number
  cy_pop_0_4_c: number
  cy_pop_5_9_c: number
  cy_pop_10_14c: number
  cy_pop_15_17c: number
  cy_pop_18_20c: number
  cy_pop_21_24c: number
  cy_pop_25_34c: number
  cy_pop_35_44c: number
  cy_pop_45_54c: number
  cy_pop_55_64c: number
  cy_pop_65_74c: number
  cy_pop_75_84c: number
  cy_pop_85p_c: number
  cy_mage_c: number
  cy_av_age_c: number
  cy_hi_l15_c: number
  cy_hi_15_25_c: number
  cy_hi_25_35_c: number
  cy_hi_35_50_c: number
  cy_hi_50_75_c: number
  cy_hi_75_100c: number
  cy_med_valuec: number
  cy_hi100_125c: number
  cy_hi125_150c: number
  cy_hi150_200c: number
  cy_hi200_250c: number
  cy_hi250_500c: number
  cy_hi_500p_c: number
  cy_med_hinc_c: number
  cy_avg_hinc_c: number
  cy_avg_valuec: number
  cy_totale: number
  cy_foodto: number
  cy_alcbev: number
  cy_housin: number
  cy_appare: number
  cy_trans: number
  cy_health: number
  cy_entrta: number
  cy_persca: number
  cy_educat: number
  cy_tobacc: number
  cy_tr: number
}

export type DemoRawResponse = DemoFields

export type DemoH3Datum = DemoFields & {
  h3_index: string
}

export type DemoH3Response = DemoH3Datum[]

export enum DemographicDataPoint {
  'GeneralOverview' = 'General Overview',
  'Overview' = 'Overview',
  'Households' = 'Households',
  'HousingUnits' = 'Housing Units',
  'Gender' = 'Gender',
  'Ethnicity' = 'Ethnicity',
  'Age' = 'Age',
  'MaritalStatus' = 'Marital Status',
  'ValueOfOwnerOccupiedHousingUnits' = 'Value of Owner Occupied Housing Units',
  'TypesOfHousingUnits' = 'Types of Housing Units',
  'HouseholdIncome' = 'Household Income',
  'Education' = 'Education',
  'LaborForce' = 'Labor Force',
  'TransportationToWork' = 'Transportation to Work',
  'TravelTimeToWork' = 'Travel Time to Work',
  'PopulationByIndustry' = 'Population by Industry',
  'PopulationByOccupation' = 'Population by Occupation',
}
